import { ClassroomCardSkeleton } from "@/components/skeletons";
import { StatPill } from "@/components/teacher/StatPill";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { AdminClassroomsDocument } from "@/generated/graphql";
import { formatYearMonthDay } from "@/i18n";
import { useDebouncedSearch } from "@/lib/utils/useDebouncedSearch";
import { ClassroomBreadcrumb } from "@/pages/teacher/ClassroomBreadcrumb";
import { LoadMoreButton, SearchBar } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { HiArrowRight } from "react-icons/hi";

const PAGE_SIZE = 24;

/**
 * The classrooms the weekly sync knows about, whoever teaches it. Scoped to one school when
 * schoolId is given; without it, every classroom, which only search makes navigable.
 */
export function AdminClassroomsPage({ schoolId }: { schoolId?: string }) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const searchText = useDebouncedSearch(searchInput);

  const { data, loading, error, fetchMore } = useQuery(AdminClassroomsDocument, {
    variables: { limit: PAGE_SIZE, offset: 0, searchText, schoolId },
  });

  const classrooms = data?.adminClassrooms.items ?? [];
  const lastSyncedAt = classrooms[0]?.syncedAt;
  // Every classroom on a scoped page shares one school, so the first name titles the page.
  const title = schoolId ? (classrooms[0]?.schoolName ?? "Classrooms") : "Classrooms";

  return (
    <TeacherPage
      title={title}
      subtitle={
        lastSyncedAt
          ? `Synced from MyBestUser on ${formatYearMonthDay("en", new Date(lastSyncedAt))}`
          : undefined
      }
      embedded
      breadcrumb={
        schoolId ? (
          <ClassroomBreadcrumb
            scope="admin"
            school={classrooms[0] ? { id: schoolId, name: classrooms[0].schoolName } : null}
          />
        ) : (
          <ClassroomBreadcrumb scope="admin" tail="All classrooms" />
        )
      }
      actions={<StatPill>{data?.adminClassrooms.total ?? 0} total</StatPill>}
    >
      <SearchBar
        value={searchInput}
        onChange={setSearchInput}
        placeholder={schoolId ? "Search by classroom" : "Search by classroom or school"}
      />

      {error && (
        <p className="app-card body p-6 text-app-overdue">Classrooms could not be loaded.</p>
      )}

      {loading && classrooms.length === 0 ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {["a", "b", "c", "d"].map((key) => (
            <ClassroomCardSkeleton key={key} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {classrooms.map((classroom) => (
            <button
              key={classroom.id}
              type="button"
              onClick={() =>
                navigate({
                  to: "/admin/classrooms/$classroomId",
                  params: { classroomId: classroom.id },
                })
              }
              className="app-card group flex flex-col gap-2 p-5 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="app-title flex-1 truncate">{classroom.name}</span>
                <HiArrowRight className="h-4 w-4 text-app-ink transition-transform group-hover:translate-x-1" />
              </div>
              {!schoolId && (
                <span className="body-sm truncate text-app-muted">{classroom.schoolName}</span>
              )}
              <StatPill>{classroom.studentCount} students</StatPill>
            </button>
          ))}

          {classrooms.length === 0 && !error && (
            <p className="app-card body-sm p-6 text-app-muted">No classrooms match that search.</p>
          )}
        </div>
      )}

      <LoadMoreButton
        hasMore={data?.adminClassrooms.hasMore ?? false}
        loading={loading}
        onClick={() =>
          void fetchMore({
            variables: { offset: classrooms.length },
            updateQuery: (previous, { fetchMoreResult }) => ({
              adminClassrooms: {
                ...fetchMoreResult.adminClassrooms,
                items: [
                  ...previous.adminClassrooms.items,
                  ...fetchMoreResult.adminClassrooms.items,
                ],
              },
            }),
          })
        }
      />
    </TeacherPage>
  );
}
